import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwbHV4dXJ5JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg3MDQ3NzAxfDA&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/admin";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, navigate, next]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(form.email, form.password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-page" className="grid lg:grid-cols-2">
      <div className="px-6 py-16 md:px-12 lg:px-20 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="max-w-md">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-soot">Seller Access</p>
          <h1 className="mt-4 font-serif text-4xl font-light tracking-tight sm:text-5xl">
            Welcome <span className="italic">back.</span>
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-soot">
            Sign in to manage your products — add new pieces, upload images, update prices and descriptions.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1.5 bg-transparent"
              />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="mt-1.5 bg-transparent"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={busy}
              className="h-12 w-full rounded-full bg-obsidian text-xs font-bold uppercase tracking-[0.18em] text-bone hover:bg-olive"
            >
              {busy ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </motion.div>
      </div>
      <div className="relative hidden overflow-hidden lg:block">
        <motion.img
          src={AUTH_IMG}
          alt="Minimal luxury interior"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    </div>
  );
}
